import { HEADSHOT_MULTIPLIER } from './constants.js';

export type WeaponId = 'smg' | 'rifle' | 'shotgun' | 'sniper';

export interface WeaponDef {
  id: WeaponId;
  name: string;
  /** Rounds per minute. */
  rpm: number;
  /** Pellets fired per trigger pull (shotguns > 1). */
  pellets: number;
  damage: number;
  /** Damage is scaled from 1 at `falloffStart` down to `falloffMin` at `falloffEnd`. */
  falloffStart: number;
  falloffEnd: number;
  falloffMin: number;
  magazine: number;
  reloadMs: number;
  /** Cone half-angle in radians at rest and at max bloom. */
  spreadMin: number;
  spreadMax: number;
  /** Spread added per shot, and recovery per second. */
  spreadPerShot: number;
  spreadRecovery: number;
  /** Vertical/horizontal camera kick per shot, in radians. */
  recoilPitch: number;
  recoilYaw: number;
  headshotMultiplier: number;
  /** Movement speed scale while firing / aiming down sights. */
  adsSpeedScale: number;
  adsFovScale: number;
}

export const WEAPONS: Record<WeaponId, WeaponDef> = {
  smg: {
    id: 'smg',
    name: 'Vector SMG',
    rpm: 900,
    pellets: 1,
    damage: 17,
    falloffStart: 18,
    falloffEnd: 45,
    falloffMin: 0.55,
    magazine: 32,
    reloadMs: 1900,
    spreadMin: 0.012,
    spreadMax: 0.07,
    spreadPerShot: 0.006,
    spreadRecovery: 0.09,
    recoilPitch: 0.011,
    recoilYaw: 0.005,
    headshotMultiplier: HEADSHOT_MULTIPLIER,
    adsSpeedScale: 0.75,
    adsFovScale: 0.85,
  },
  rifle: {
    id: 'rifle',
    name: 'AK Rifle',
    rpm: 600,
    pellets: 1,
    damage: 26,
    falloffStart: 30,
    falloffEnd: 70,
    falloffMin: 0.7,
    magazine: 30,
    reloadMs: 2300,
    spreadMin: 0.006,
    spreadMax: 0.06,
    spreadPerShot: 0.007,
    spreadRecovery: 0.08,
    recoilPitch: 0.018,
    recoilYaw: 0.007,
    headshotMultiplier: HEADSHOT_MULTIPLIER,
    adsSpeedScale: 0.6,
    adsFovScale: 0.7,
  },
  shotgun: {
    id: 'shotgun',
    name: 'Breacher',
    rpm: 75,
    pellets: 9,
    damage: 13,
    falloffStart: 6,
    falloffEnd: 22,
    falloffMin: 0.15,
    magazine: 6,
    reloadMs: 2800,
    spreadMin: 0.055,
    spreadMax: 0.09,
    spreadPerShot: 0.01,
    spreadRecovery: 0.12,
    recoilPitch: 0.05,
    recoilYaw: 0.01,
    headshotMultiplier: 1.4,
    adsSpeedScale: 0.8,
    adsFovScale: 0.95,
  },
  sniper: {
    id: 'sniper',
    name: 'Longshot',
    rpm: 45,
    pellets: 1,
    damage: 90,
    falloffStart: 200,
    falloffEnd: 250,
    falloffMin: 0.9,
    magazine: 5,
    reloadMs: 3000,
    spreadMin: 0.0,
    spreadMax: 0.12,
    spreadPerShot: 0.06,
    spreadRecovery: 0.2,
    recoilPitch: 0.09,
    recoilYaw: 0.008,
    headshotMultiplier: HEADSHOT_MULTIPLIER,
    adsSpeedScale: 0.45,
    adsFovScale: 0.35,
  },
};

export const WEAPON_ORDER: readonly WeaponId[] = ['smg', 'rifle', 'shotgun', 'sniper'];

export const weaponIndex = (id: WeaponId): number => WEAPON_ORDER.indexOf(id);

export const weaponFromIndex = (index: number): WeaponId =>
  WEAPON_ORDER[index] ?? 'smg';

/** Milliseconds between shots. */
export const fireIntervalMs = (def: WeaponDef): number => 60_000 / def.rpm;

/** Damage after distance falloff. */
export const damageAtDistance = (def: WeaponDef, distance: number): number => {
  if (distance <= def.falloffStart) return def.damage;
  if (distance >= def.falloffEnd) return def.damage * def.falloffMin;
  const t = (distance - def.falloffStart) / (def.falloffEnd - def.falloffStart);
  return def.damage * (1 - t * (1 - def.falloffMin));
};
